/**
 * Counters for the offline sync subsystem.
 *
 * A sync engine fails quietly: a cursor that stopped advancing, a reseed loop,
 * a drift check that never passes — none of them raise an error, and all of them
 * look exactly like "nothing changed today". These counters are what separate a
 * healthy system from one that silently stopped converging.
 *
 * In-memory and per-session on purpose. This is an operator aid on the Offline
 * Data screen, not an audit trail, and persisting it would compete for the very
 * quota the storage lifecycle is trying to protect.
 */

const MAX_RECENT = 20;

const state = {
    counts: Object.create(null),
    recent: [],
    startedAt: new Date().toISOString(),
};

const listeners = new Set();

function notify() {
    for (const listener of listeners) {
        try {
            listener(getSyncTelemetry());
        } catch (error) {
            console.warn('[syncTelemetry] listener failed', error);
        }
    }
}

/**
 * @param {string} event  e.g. 'replay', 'reseed', 'drift_detected'
 * @param {object} detail small, non-sensitive context
 */
export function recordSyncEvent(event, detail = {}) {
    if (!event) return;

    state.counts[event] = (state.counts[event] ?? 0) + 1;
    state.recent.unshift({ event, detail, at: new Date().toISOString() });
    if (state.recent.length > MAX_RECENT) state.recent.length = MAX_RECENT;

    notify();
}

export function getSyncTelemetry() {
    return {
        startedAt: state.startedAt,
        counts: { ...state.counts },
        recent: [...state.recent],
    };
}

export function onSyncTelemetry(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function resetSyncTelemetry() {
    state.counts = Object.create(null);
    state.recent = [];
    state.startedAt = new Date().toISOString();
    notify();
}

export default { recordSyncEvent, getSyncTelemetry, onSyncTelemetry, resetSyncTelemetry };
